import {DataStatus, Dialog} from 'platform/components';
import {Show} from 'platform/foundation';

import {useEffect, useRef, useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {LabeledKey} from '@dms/api/shared';
import {
  GetOrderItemForMaterialApiResponse,
  GetServiceOrderJobItemWorkApiResponse,
  useLazyGetOrderItemForMaterialQuery,
  useLazyGetServiceOrderJobItemWorkQuery,
  usePatchOrderItemForMaterialMutation,
  usePatchOrderItemForWorkMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';
import {EditMaterialItem} from '@dms/shared-aftersales';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {ControlledRowControl, type RowV5ResponseBody} from 'features/eag-grid';

import {MATERIAL_TYPE} from '../constants/materialType';
import {EditLabourItem} from './EditLabourItem/EditLabourItem';

export type Item = (GetServiceOrderJobItemWorkApiResponse | GetOrderItemForMaterialApiResponse) & {
  rowId: string;
  jobId: string;
  priceType: string;
};

export type ReceiveJobItemRowData = {
  itemName: {value: string};
  itemType: {value: LabeledKey};
  itemPriceType: {value: LabeledKey};
  itemGoodwillRatio: {value: {label: string} | Nullish};
  serviceOrderJobItemId: {value: string};
  serviceJobId: {value: string};
  assignMechanics: {value: {key: string}[]};
};

export type RequestEditDialogProps = {
  gridCode: string;
  rowId?: string;
  serviceCaseId: string;
  orderId: string;
  itemData?: ReceiveJobItemRowData;
  dataQueryId?: string;
  itemType?: string;
  onClose: () => void;
} & RequiredTestIdProps;

export function RequestEditDialog(props: RequestEditDialogProps) {
  const [item, setItem] = useState<Item | null>(null);

  const [getMaterialItem] = useLazyGetOrderItemForMaterialQuery();
  const [getLabourItem] = useLazyGetServiceOrderJobItemWorkQuery();

  const [, patchMaterialItemStatus] = usePatchOrderItemForMaterialMutation({
    fixedCacheKey: 'edit-service-order-item',
  });

  const [, patchLabourItemStatus] = usePatchOrderItemForWorkMutation({
    fixedCacheKey: 'edit-service-order-work-item',
  });

  const isSavedByRowControlRef = useRef<boolean>(false);

  const materialItemFormRef = useRef<{
    submit: () => Promise<boolean>;
    isDirty: boolean;
  } | null>(null);

  const labourItemFormRef = useRef<{
    submit: () => Promise<boolean>;
    isDirty: boolean;
  } | null>(null);

  const isSavingChanges = patchMaterialItemStatus.isLoading || patchLabourItemStatus.isLoading;

  const isItemLabour = item && 'workType' in item;
  const isItemMaterial = item && 'warehouse' in item;

  const handleGetItemDetail = async (rowId: string, item: ReceiveJobItemRowData) => {
    const serviceItemId = item.serviceOrderJobItemId.value;
    const serviceJobId = item.serviceJobId.value;

    let response: Omit<Item, 'rowId' | 'priceType' | 'jobId'> | Nullish | void = null;

    if (item.itemType.value.key === MATERIAL_TYPE) {
      response = await getMaterialItem({
        serviceCaseId: props.serviceCaseId,
        serviceOrderId: props.orderId,
        serviceJobId,
        serviceItemId,
      })
        .unwrap()
        .catch(handleApiError);
    } else {
      response = await getLabourItem({
        serviceCaseId: props.serviceCaseId,
        serviceOrderId: props.orderId,
        serviceJobId,
        serviceItemId,
      })
        .unwrap()
        .catch(handleApiError);
    }

    setItem({...response, rowId, priceType: item.itemPriceType.value.label, jobId: serviceJobId});
  };

  useEffect(
    function loadItemDetail() {
      if (props.rowId && props.itemData?.serviceOrderJobItemId?.value) {
        handleGetItemDetail(props.rowId, {...props.itemData});
      }
    },
    // Disabled exhaustive-deps because handleGetItemDetail only depends on stable values.
    // eslint-disable-next-line react/exhaustive-deps
    [props.rowId, props.itemData]
  );

  const handlePageChange = async (row: RowV5ResponseBody) => {
    // Needed because of how workshop work with row data.
    // eslint-disable-next-line eslint-js/no-restricted-syntax
    const castedRowData = row.cells as unknown as ReceiveJobItemRowData;

    const isDirty = isItemMaterial
      ? materialItemFormRef.current?.isDirty
      : labourItemFormRef.current?.isDirty;

    const submitForm = isItemMaterial
      ? materialItemFormRef.current?.submit
      : labourItemFormRef.current?.submit;

    // No changes done, continue to next item.
    if (!isDirty && row.id && castedRowData) {
      handleGetItemDetail(row.id, castedRowData);
      return true;
    }

    isSavedByRowControlRef.current = true;

    const isSubmitSuccessfull = await submitForm?.();

    const hasApiError = isItemMaterial
      ? patchMaterialItemStatus.isError
      : patchLabourItemStatus.isError;

    // Validation failed or API error occured, stay on current item.
    if (!isSubmitSuccessfull || hasApiError) {
      isSavedByRowControlRef.current = false;
      return false;
    }

    // Intentionally not awaited — pagination advances immediately after save,
    // while the next item fetches in the background.
    row.id && castedRowData && handleGetItemDetail(row.id, castedRowData);
    return true;
  };

  return (
    <>
      <Dialog
        size="large"
        title={
          isItemMaterial
            ? i18n.t('entity.warehouse.labels.editMaterial')
            : i18n.t('entity.warehouse.labels.editLabour')
        }
        isOpen={isNotNil(props.rowId)}
        scrollBehavior="inside"
        onClose={props.onClose}
        pagination={
          <Show when={isNotNil(props.dataQueryId) && isNotNil(item)}>
            <ControlledRowControl
              isDisabled={isSavingChanges}
              dataQueryId={props.dataQueryId as string}
              gridCode={props.gridCode}
              entityId={item?.rowId as string}
              onPageChange={handlePageChange}
            />
          </Show>
        }
        withAdditionalFooter
        data-testid={props['data-testid']}
      >
        <DataStatus
          isLoading={isNil(item)}
          isError={isNotNil(item) && !isItemLabour && !isItemMaterial}
        >
          <Show when={isItemMaterial}>
            <EditMaterialItem
              itemId={item?.id ?? ''}
              serviceCaseId={props.serviceCaseId}
              serviceOrderId={props.orderId ?? ''}
              serviceJobId={item?.jobId ?? ''}
              isSavedByRowControlRef={isSavedByRowControlRef}
              formRef={materialItemFormRef}
              onClose={props.onClose}
              data-testid={suffixTestId('editMaterialItem', props)}
            />
          </Show>

          <Show when={isItemLabour}>
            <EditLabourItem
              itemId={item?.id ?? ''}
              serviceCaseId={props.serviceCaseId}
              serviceOrderId={props.orderId ?? ''}
              serviceJobId={item?.jobId ?? ''}
              isSavedByRowControlRef={isSavedByRowControlRef}
              formRef={labourItemFormRef}
              onClose={props.onClose}
              data-testid={suffixTestId('editJobItem', props)}
            />
          </Show>
        </DataStatus>
      </Dialog>
    </>
  );
}
